// src/utils/version-watcher.js
import { ref } from '@vue/composition-api'
import axios from 'axios'
import Vue from 'vue'
import { APP_VERSION_STORAGE_KEY, migrateLegacyAppVersionStorageKey } from '@/utils/storage-keys'

/* import Swal from 'sweetalert2' */
const isUpdating = ref(false)
const pollTimer = ref(null)
const rePromptTimer = ref(null)
const rePromptInterval = ref(null)
const versionInfo = ref({
  currentVersion: null,
  latestVersion: null,
  timestamp: null,
  buildTime: null,
  files: [],
  ready: true,
})
const newVersionAvailable = ref(false)
const isModalShowing = ref(false)
const isCheckingVersion = ref(false)
const pendingNavigation = ref(false)
const lastUserTriggeredCheckMs = ref(0)
const candidateVersion = ref(null)
const candidateFirstSeenMs = ref(0)
const stabilizationMs = Number(process.env.VUE_APP_VERSION_STABILIZE_MS || 10000)
const buildVersion = process.env.VUE_APP_VERSION || '1.0.0'
const defaultPollMs = Number(process.env.VUE_APP_VERSION_POLL_MS || 60000)

// Eski 'app_version' key'inden yeni projeye-özel key'e kopyala (varsa).
migrateLegacyAppVersionStorageKey()

if (!versionInfo.value.currentVersion) {
  versionInfo.value.currentVersion = buildVersion
}

// ---------------- HELPERS ----------------
function resolveVersionJsonUrl() {
  // 1) Manifest varsa ondan türet
  const manifestHref = document.querySelector('link[rel="manifest"]')?.getAttribute('href')
  if (manifestHref) {
    const baseFromManifest = manifestHref.replace(/manifest\.json.*$/i, '').replace(/\/+$/g, '')

    return `${baseFromManifest}/version.json`
  }

  // 2) Build-time BASE_URL/VUE_APP_ROUTE (Vue CLI derlemede stringe gömer)
  const envBase = (process.env.BASE_URL || process.env.VUE_APP_ROUTE || '').replace(/\/+$/g, '')
  if (envBase) return `${envBase}/version.json`

  // 3) URL'den ilk segmenti al (örn. /test2)
  const path = window.location.pathname
  const firstSeg = path.split('/').filter(Boolean)[0]
  const prefix = firstSeg ? `/${firstSeg}` : ''

  return `${prefix}/version.json`
}

// Asset readiness kontrolü kaldırıldı; sadece versiyon karşılaştırması kullanılacak

async function fetchVersionJson() {
  try {
    const response = await axios.get(resolveVersionJsonUrl(), {
      params: { t: Date.now(), v: Math.random().toString(36).substring(2, 10) },
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Expires: '0',
        Accept: 'application/json',
      },
      timeout: 5000,
    })
    const { data } = response
    if (!data || typeof data !== 'object' || !data.version) return null

    return data
  } catch (e) {
    return null
  }
}

// ---------------- UPDATE ----------------
export async function handleUpdate() {
  if (isUpdating.value) return
  isUpdating.value = true

  // "Şimdi Güncelle" dediğinde localStorage'a yeni versiyonu kaydet
  // Bu sayede "Daha Sonra" sonrası modal tekrar çıkabilir
  if (versionInfo.value?.latestVersion) {
    localStorage.setItem(APP_VERSION_STORAGE_KEY, versionInfo.value.latestVersion)
    console.log('localStorage güncellendi:', versionInfo.value.latestVersion)
  }

  // Tekrar hatırlatma zamanlayıcısını temizle
  if (rePromptTimer.value) {
    clearTimeout(rePromptTimer.value)
    rePromptTimer.value = null
  }
  if (rePromptInterval.value) {
    clearInterval(rePromptInterval.value)
    rePromptInterval.value = null
  }

  // SweetAlert2 ile şık bir güncelleme ekranı göster
  try {
    Vue.swal.fire({
      title: 'Güncelleniyor...',
      html: '<div style="text-align: center;"><img src="/apple-icon-152x152.png" alt="Aslı Logo" style="width: 80px; height: auto; margin-bottom: 20px;" class="pulse-logo"><div class="progress-bar-container" style="width: 100%; height: 6px; background-color: #2d3a5d; border-radius: 3px; overflow: hidden; margin: 20px auto; position: relative;"><div class="progress-bar" style="position: absolute; top: 0; left: 0; height: 100%; width: 100%; background: linear-gradient(90deg, #00d0bd, #7367f0); animation: swal-progress 2s infinite linear; border-radius: 3px;"></div></div><div class="mt-3">Yeni sürüm yükleniyor, lütfen bekleyiniz...</div></div>',
      showConfirmButton: false,
      allowOutsideClick: false,
      showCloseButton: false,
      background: '#211e2a',
      color: '#fff',
      backdrop: 'rgba(33, 30, 42, 0.95)',
      didOpen: () => {
        // CSS animasyonu ekle
        const style = document.createElement('style')
        style.textContent = `
          @keyframes swal-progress {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }

          @keyframes pulse-animation {
            0% { transform: scale(0.9); opacity: 0.7; }
            50% { transform: scale(1.1); opacity: 1; }
            100% { transform: scale(0.9); opacity: 0.7; }
          }

          .pulse-logo {
            animation: pulse-animation 1.5s infinite ease-in-out;
          }
        `
        document.head.appendChild(style)

        // Güncelleme işlemini başlat
        const updateApp = () => {
          // Uygulamayı yeniden yüklemeden önce meta tag'lerini güncelle
          const metaThemeColor = document.querySelector('meta[name="theme-color"]')
          if (metaThemeColor) metaThemeColor.setAttribute('content', '#211e2a')

          // Sayfayı yeniden yüklemeden önce arkaplanı ayarla
          document.body.style.backgroundColor = '#211e2a'
          document.documentElement.style.backgroundColor = '#211e2a'

          // Sayfa yüklenirken gösterilecek stil
          const loadingStyle = document.createElement('style')
          loadingStyle.textContent = `
            body::before {
              content: '';
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background-color: #211e2a;
              z-index: 9999999;
              display: flex;
              justify-content: center;
              align-items: center;
            }
          `
          document.head.appendChild(loadingStyle)

          // Cache'i temizle ve sayfayı yenile
          localStorage.removeItem('updateLoadCount')
          window.location.reload(true)
        }

        // 3 saniye sonra güncelleme işlemini başlat
        setTimeout(updateApp, 3000)
      },
    })
  } catch (error) {
    console.error('Güncelleme hatası:', error)

    // Hata durumunda basit bir yöntem ile yenile
    localStorage.removeItem('updateLoadCount')
    window.location.reload(true)
  }
}

// ---------------- UPDATE MODAL ----------------
export async function showUpdateAlert() {
  if (isUpdating.value || isModalShowing.value) return
  isModalShowing.value = true

  try {
    const localStoredVersion = localStorage.getItem(APP_VERSION_STORAGE_KEY) || ''
    const debugBuildTime = versionInfo.value?.buildTime || ''
    const debugTimestamp = versionInfo.value?.timestamp || ''

    // Yeni versiyon modalı
    const result = await Vue.swal.fire({
      title: 'Yeni Versiyon Mevcut!',
      html: `
        <div class="mb-3">Uygulamanın yeni bir versiyonu mevcut.</div>
        <div class="text-left mb-3">
          <div><strong>Mevcut versiyon:</strong> v${versionInfo.value?.currentVersion}</div>
          <div><strong>Yeni versiyon:</strong> v${versionInfo.value?.latestVersion}</div>
        </div>
        <div style="display:flex;justify-content:center;margin-top:6px;margin-bottom:2px;">
          <button
            type="button"
            id="version-debug-toggle"
            aria-label="Versiyon bilgisi"
            style="width:26px;height:26px;border-radius:50%;border:1px solid #2196f3;background:#e3f2fd;color:#2196f3;cursor:pointer;font-weight:700;line-height:24px;padding:0;"
          >i</button>
        </div>
        <div style="margin-top:16px;color:#ff9800;font-weight:500;">
          Not: Güncelleme yapılana kadar her işlem öncesinde bu mesaj görüntülenecektir.
        </div>
        <div
          id="version-debug-panel"
          style="display:none;margin-top:12px;padding:10px 12px;border:1px solid #e0e0e0;background:#fafafa;border-radius:10px;text-align:left;font-size:12px;line-height:1.45;color:#333;"
        >
          <div style="font-weight:700;margin-bottom:6px;">Versiyon Detayları</div>
          <div style="font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;">
            <div><strong>APP_VERSION_STORAGE_KEY</strong>: ${APP_VERSION_STORAGE_KEY}</div>
            <div><strong>Local (current)</strong>: ${versionInfo.value?.currentVersion || '-'}</div>
            <div><strong>Server (latest)</strong>: ${versionInfo.value?.latestVersion || '-'}</div>
            <div><strong>buildTime</strong>: ${debugBuildTime || '-'}</div>
          </div>
        </div>
      `,
      icon: 'info',
      iconColor: '#2196f3',
      customClass: {
        popup: 'swal-update-modal',
      },
      showCancelButton: true,
      confirmButtonText: 'Şimdi Güncelle',
      cancelButtonText: 'Daha Sonra',
      confirmButtonColor: '#4CAF50',
      cancelButtonColor: '#FF5252',
      allowOutsideClick: false,
      showCloseButton: false,
      background: '#ffffff',
      color: '#333333',
      backdrop: 'rgba(0, 0, 0, 0.5)',
      imageWidth: 100,
      imageHeight: 100,
      didOpen: () => {
        // Bazı kurulumlarda iconColor border'ı etkilemeyebiliyor; burada sadece bu modal için garanti altına alıyoruz.
        const popup = document.querySelector('.swal2-popup.swal-update-modal') || document.querySelector('.swal2-popup')
        const iconEl = popup?.querySelector('.swal2-icon.swal2-info')
        const iconContentEl = iconEl?.querySelector('.swal2-icon-content')

        if (iconEl) {
          iconEl.style.borderColor = '#2196f3'
          iconEl.style.color = '#2196f3'
        }
        if (iconContentEl) {
          iconContentEl.style.color = '#2196f3'
        }

        // Debug panel toggle (sadece bu modal)
        const toggleBtn = popup?.querySelector('#version-debug-toggle')
        const panel = popup?.querySelector('#version-debug-panel')
        if (toggleBtn && panel) {
          toggleBtn.addEventListener('click', () => {
            panel.style.display = panel.style.display === 'none' ? 'block' : 'none'
          })
        }
      },
    })

    if (result.isConfirmed) {
      // Güncelleme onaylandıysa, pendingNavigation'ı kapat ve güncellemeyi başlat
      pendingNavigation.value = false
      await handleUpdate()
    } else {
      // Daha sonra denilirse, pendingNavigation'ı aktif et (süre sınırı olmadan)
      pendingNavigation.value = true

      // Zamanlayıcı kaldırıldı - kullanıcı güncelleme yapana kadar engel kalacak

      // 10 sn'de bir tekrar hatırlat (sürüm değiştiği sürece)
      if (rePromptInterval.value) clearInterval(rePromptInterval.value)
      rePromptInterval.value = setInterval(async () => {
        try {
          // Modal görünüyorsa veya güncelleme yapılıyorsa atlayalim
          if (isUpdating.value || isModalShowing.value) return

          console.log('Tekrar modal kontrolü yapılıyor...')
          const data = await fetchVersionJson()
          if (!data) return
          const fetchedVersion = data.version

          // Mevcut versiyon localStorage'dan alınıyor, güncel versiyon server'dan
          const storedVersion = localStorage.getItem(APP_VERSION_STORAGE_KEY) || buildVersion
          console.log('Mevcut versiyon (localStorage):', storedVersion)
          console.log('Server versiyon:', fetchedVersion)

          // Önemli: versionInfo.currentVersion'u localStorage'dan alınan değerle güncelle
          versionInfo.value.currentVersion = storedVersion
          versionInfo.value.latestVersion = fetchedVersion
          versionInfo.value.timestamp = data?.timestamp
          versionInfo.value.buildTime = data?.buildTime

          // Versiyon değişimi kontrolü - localStorage'daki versiyonu güncellemeyelim
          // böylece "Daha Sonra" sonrası modal tekrar çıkabilir
          const changed = fetchedVersion !== storedVersion

          if (changed) {
            console.log('Sürüm değişimi tespit edildi, modal tekrar gösterilecek')

            // Modal görünmüyorsa göster
            if (!isModalShowing.value) {
              // Direkt showUpdateAlert çağır
              await showUpdateAlert()
            }
          } else if (rePromptInterval.value) {
            console.log('Sürüm eşleşti, interval temizleniyor')
            clearInterval(rePromptInterval.value)
            rePromptInterval.value = null
          }
        } catch (e) {
          console.error('Tekrar modal kontrol hatası:', e)
        }
      }, 10000)
    }
  } catch (e) {
    console.error('Update modal error:', e)
  } finally {
    // Modal kapandığında hemen değil, 100ms sonra flag'i kapat
    // Böylece rePromptInterval'in if kontrolünden geçebilir
    setTimeout(() => {
      isModalShowing.value = false
    }, 100)
  }
}

// ---------------- VERSION CHECK ----------------
async function checkForNewVersion({ showAlert } = { showAlert: true }) {
  if (isCheckingVersion.value) return false
  isCheckingVersion.value = true

  try {
    const data = await fetchVersionJson()
    if (!data) return false
    const fetchedVersion = data.version

    // 📌 LocalStorage'dan mevcut versiyonu oku
    const storedVersion = localStorage.getItem(APP_VERSION_STORAGE_KEY)
    let effectiveCurrentVersion = storedVersion

    // Eğer localStorage'da yoksa, buildVersion (1.0.0) kullan
    if (!storedVersion) {
      effectiveCurrentVersion = buildVersion
    }

    // latestVersion her zaman server’dan gelen versiyon
    versionInfo.value.latestVersion = fetchedVersion

    // currentVersion = localStorage’daki (önceki) versiyon (yoksa fetched)
    versionInfo.value.currentVersion = effectiveCurrentVersion || buildVersion

    versionInfo.value.files = []
    versionInfo.value.timestamp = data?.timestamp
    versionInfo.value.buildTime = data?.buildTime
    versionInfo.value.ready = true

    let isVersionChanged = fetchedVersion !== versionInfo.value.currentVersion

    // Yeni versiyon ilk kez görülüyorsa stabilize penceresini başlat
    if (isVersionChanged) {
      const now = Date.now()
      if (candidateVersion.value !== fetchedVersion) {
        candidateVersion.value = fetchedVersion
        candidateFirstSeenMs.value = now
        isVersionChanged = false // şimdilik modal/engel gösterme
      } else if (now - candidateFirstSeenMs.value < stabilizationMs) {
        isVersionChanged = false // stabilize süresi dolmadı
      }
    }

    // Versiyon değiştiğinde localStorage'a yazmayı engelleyelim
    // "Daha Sonra" dediğinde localStorage güncellenmeyecek
    // "Şimdi Güncelle" dediğinde handleUpdate() içinde güncellenecek

    // ⚠️ ÖNEMLİ: localStorage'a otomatik yazma yok

    // Sadece versiyon değişmişse ve uyarı isteniyorsa modal göster
    if (showAlert && isVersionChanged) {
      await showUpdateAlert()
    }

    newVersionAvailable.value = isVersionChanged

    return isVersionChanged
  } catch (e) {
    console.error('Versiyon kontrol hatası:', e)

    return false
  } finally {
    isCheckingVersion.value = false
  }
}

// ---------------- WATCHER ----------------
export async function startVersionWatcher({ intervalMs = defaultPollMs, isFirstLogin = false } = {}) {
  console.log('startVersionWatcher çağrıldı')

  // İlk giriş kontrolü (parametre veya localStorage'da app_version yoksa ilk giriştir)
  const isFirstLoginCheck = isFirstLogin || !localStorage.getItem(APP_VERSION_STORAGE_KEY)
  console.log('İlk giriş kontrolü:', isFirstLoginCheck)

  // Sunucudan versiyon bilgisini al
  const data = await fetchVersionJson()
  if (!data) return
  const fetchedVersion = data.version
  console.log('Sunucudan alınan versiyon:', fetchedVersion)

  // İlk giriş kontrolü
  if (isFirstLoginCheck) {
    // İlk giriş ve versiyon 1.0.0 ise
    if (buildVersion === '1.0.0') {
      // Sunucudan gelen versiyonu localStorage'a kaydet
      localStorage.setItem(APP_VERSION_STORAGE_KEY, fetchedVersion)
      console.log('İlk giriş - localStorage güncellendi:', fetchedVersion)

      // Mevcut versiyon değerini de güncelle
      versionInfo.value.currentVersion = fetchedVersion
      versionInfo.value.latestVersion = fetchedVersion

      // Modal gösterme
      console.log('İlk giriş - modal gösterilmeyecek')

      // Sayfa yenileme yapmıyoruz, VerticalNavMenu için farklı bir yöntem kullanacağız
      console.log('İlk giriş - sayfa yenilemeden devam ediliyor')
    } else {
      // İlk giriş ama versiyon 1.0.0 değil, mevcut versiyonu kaydet
      localStorage.setItem(APP_VERSION_STORAGE_KEY, buildVersion)
      console.log('İlk giriş (1.0.0 değil) - localStorage güncellendi:', buildVersion)

      // Normal versiyon kontrolü yap
      await checkForNewVersion({ showAlert: true })
    }
  } else {
    // İlk giriş değil, normal versiyon kontrolü
    await checkForNewVersion({ showAlert: true })
  }

  // Zamanlayıcıyı ayarla
  if (pollTimer.value) clearInterval(pollTimer.value)
  if (intervalMs > 0) pollTimer.value = setInterval(() => checkForNewVersion({ showAlert: true }), intervalMs)
}

export function stopVersionWatcher() {
  if (pollTimer.value) clearInterval(pollTimer.value)

  pollTimer.value = null
}

// ---------------- ROUTER GUARD ----------------
export function installVersionGuards(router) {
  if (!router || !router.beforeEach) return

  router.beforeEach(async (to, from, next) => {
    const isLoggedIn = localStorage.getItem('accessToken') && localStorage.getItem('userData')
    const isLoginPage = to.name === 'auth-login' || to.path === '/login'

    // Kullanıcı giriş yapmışsa ve login sayfasında değilse versiyon kontrolü yap
    if (isLoggedIn && !isLoginPage) {
      try {
        const now = Date.now()
        if (now - lastUserTriggeredCheckMs.value > 10000) {
          lastUserTriggeredCheckMs.value = now

          // Sunucudan versiyon bilgisini al
          const data = await fetchVersionJson()
          if (!data) return next()
          const fetchedVersion = data.version

          // LocalStorage'dan mevcut versiyonu al
          const storedVersion = localStorage.getItem(APP_VERSION_STORAGE_KEY)

          console.log('Route geçişi - Sunucu versiyon:', fetchedVersion)
          console.log('Route geçişi - LocalStorage versiyon:', storedVersion)

          // İlk giriş kontrolü (app_version yoksa ilk giriştir)
          const isFirstLoginRoute = !storedVersion

          if (isFirstLoginRoute) {
            // İlk giriş ve versiyon 1.0.0 ise
            if (buildVersion === '1.0.0') {
              // Sunucudan gelen versiyonu localStorage'a kaydet
              localStorage.setItem(APP_VERSION_STORAGE_KEY, fetchedVersion)
              console.log('İlk route geçişi - localStorage güncellendi:', fetchedVersion)

              // Mevcut versiyon değerini de güncelle
              versionInfo.value.currentVersion = fetchedVersion
              versionInfo.value.latestVersion = fetchedVersion

              // Sayfa yenileme yapmıyoruz, sadece devam ediyoruz
              console.log('İlk route geçişi - sayfa yenilemeden devam ediliyor')

              // Normal versiyon kontrolünü atla
              return next()
            }

            // İlk giriş ama versiyon 1.0.0 değil, mevcut versiyonu kaydet
            localStorage.setItem(APP_VERSION_STORAGE_KEY, buildVersion)
            console.log('İlk route geçişi (1.0.0 değil) - localStorage güncellendi:', buildVersion)
          }

          // Normal versiyon kontrolü - her zaman yap
          const hasUpdate = await checkForNewVersion({ showAlert: true })
          console.log('Route geçişi sırasında versiyon kontrolü:', hasUpdate ? 'Güncelleme var' : 'Güncelleme yok')

          // Güncelleme varsa ve pendingNavigation aktifse, gezinmeyi engelle
          if (hasUpdate && pendingNavigation.value) {
            console.log('Route engellendi: Güncelleme gerekiyor')

            return next(false)
          }
        }
      } catch (error) {
        console.error('Route geçişi sırasında versiyon kontrol hatası:', error)
      }
    }

    // Yukarıdaki kontrol sonrası hala pendingNavigation aktifse ve login sayfasında değilsek engelle
    if (pendingNavigation.value && !isLoginPage) {
      console.log('Route engellendi: pendingNavigation aktif')

      // Güncelleme modalini göster
      await Vue.swal.fire({
        icon: 'warning',
        title: 'Yeni Versiyon Güncellenmeli!',
        text: 'Yeni bir güncelleme mevcut. Güncelleme yapılana kadar başka sayfaya geçemezsiniz.',
        showConfirmButton: true,
        confirmButtonText: 'Tamam',
        confirmButtonColor: '#4CAF50',
        allowOutsideClick: false,
        showCloseButton: false,
        background: '#ffffff',
        color: '#333333',
        backdrop: 'rgba(0, 0, 0, 0.5)',
      })

      // Tekrar versiyon kontrolü yap
      await checkForNewVersion({ showAlert: true })

      return next(false)
    }

    // Tüm kontroller geçti, devam et
    console.log('Route geçişine izin verildi')
    next()

    return undefined
  })

  window.addEventListener('focus', async () => {
    const isLoggedIn = localStorage.getItem('accessToken') && localStorage.getItem('userData')
    if (isLoggedIn) await checkForNewVersion({ showAlert: true })

    return undefined
  })
}

export { newVersionAvailable, versionInfo, pendingNavigation }
